import { computed, toValue, watch } from "vue";

import { useHostElement } from "#ce/composables/useHostElement";

// ARIA element reflection, the only way a control in a shadow root can reference
// a labelling element outside its own tree.
const SUPPORTS_ELEMENT_REFLECTION = "ariaLabelledByElements" in Element.prototype;

/**
 * Names a control rendered inside the shadow root from the host element's ARIA attributes.
 *
 * A host `aria-label` never reaches a descendant in the shadow root, and an
 * `aria-labelledby` IDREF does not resolve across the shadow boundary. The referenced
 * elements are resolved against the host's own tree and attached to the target through
 * ARIA element reflection; where reflection is unsupported their text is used as the
 * target's `aria-label` instead.
 *
 * @param {import('vue').MaybeRefOrGetter<Element|null|undefined>} target - Control to name.
 * @param {object} sources
 * @param {import('vue').MaybeRefOrGetter<string|undefined>} sources.label - The host's `aria-label`.
 * @param {import('vue').MaybeRefOrGetter<string|undefined>} sources.labelledby - The host's `aria-labelledby`.
 * @param {import('vue').MaybeRefOrGetter<Element[]|undefined>} [sources.elements] - Elements labelling the
 *   host by other means, such as the `<label>` elements of a form-associated host. Used only when the
 *   host carries neither `aria-labelledby` nor `aria-label`, the order the accessible name is computed in.
 * The target's `aria-labelledby` is shared, and the two directions differ. A withdrawal takes only
 * the reference this composable attached, so a control named by an `<sp-label>` in the same shadow
 * root keeps that name. An attachment replaces what is there, because two names on one control is
 * a conflict; a reference the composable was not given is reported in development first.
 *
 * @returns {{ ariaLabel: import('vue').ComputedRef<string|undefined>, elementsText: import('vue').ComputedRef<string|undefined> }}
 *   `ariaLabel` is the `aria-label` to bind on the target. `elementsText` is the joined text of `elements`,
 *   whatever the host's ARIA attributes say.
 */
export function useAriaLabel(target, { label, labelledby, elements }) {
  const host = useHostElement();

  const referencedElements = computed(() => {
    const ids = toValue(labelledby)?.trim();

    if (!ids || !host) {
      return [];
    }

    const root = host.getRootNode();

    return ids
      .split(/\s+/)
      .map((id) => root.getElementById?.(id))
      .filter(Boolean);
  });

  const labellingElements = computed(() => {
    if (referencedElements.value.length > 0) {
      return referencedElements.value;
    }

    return toValue(label) ? [] : (toValue(elements) ?? []);
  });

  const labellingText = computed(() => textOf(labellingElements.value));

  const elementsText = computed(() => textOf(toValue(elements) ?? []));

  const ariaLabel = computed(() => {
    if (SUPPORTS_ELEMENT_REFLECTION) {
      return toValue(label);
    }

    // aria-labelledby outranks aria-label in the accessible name computation.
    return labellingText.value ?? toValue(label);
  });

  // The element this composable last attached a reference to, and the elements it put there, so a
  // withdrawal takes only its own and leaves a co-writer's reference standing.
  let attachedTo = null;
  let attachedElements = [];

  watch(
    [() => toValue(target), labellingElements],
    ([element, elements], previous) => {
      if (!SUPPORTS_ELEMENT_REFLECTION) {
        return;
      }

      detach(previous?.[0] === element ? null : previous?.[0]);

      if (!element) {
        return;
      }

      if (elements.length === 0) {
        detach(element);
        return;
      }

      warnOnForeignReference(element, elements);
      element.ariaLabelledByElements = elements;
      attachedTo = element;
      attachedElements = elements;
    },
    { immediate: true, flush: "post" },
  );

  function detach(element) {
    if (!element || attachedTo !== element) {
      return;
    }

    const remaining = (element.ariaLabelledByElements ?? []).filter(
      (candidate) => !attachedElements.includes(candidate),
    );

    element.ariaLabelledByElements = remaining.length > 0 ? remaining : null;
    attachedTo = null;
    attachedElements = [];
  }

  // Element reflection reflects an empty `aria-labelledby`, so a non-empty one is an IDREF another
  // component wrote. Replacing one silently is how a control ends up with no name at all — but a
  // label this composable is given, now or at its last attachment, is naming the control with it
  // rather than against it.
  function warnOnForeignReference(element, elements) {
    if (!import.meta.env?.DEV) {
      return;
    }

    const owned = new Set([...elements, ...attachedElements].map((candidate) => candidate.id).filter(Boolean));
    const foreign = (element.getAttribute("aria-labelledby") ?? "").split(/\s+/).filter((id) => id && !owned.has(id));

    if (foreign.length === 0) {
      return;
    }

    console.warn(
      "[useAriaLabel] The target already carries an aria-labelledby written elsewhere, which naming it here replaces. " +
        "Pass those elements in through `elements` instead.",
      element,
    );
  }

  return {
    ariaLabel,
    elementsText,
  };
}

function textOf(elements) {
  const text = elements
    .map((element) => element.textContent?.trim())
    .filter(Boolean)
    .join(" ");

  return text || undefined;
}
